import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Blocks, Crosshair } from "lucide-react";
import AppShell from "../components/AppShell";
import MascotCoach from "../components/MascotCoach";
import { useProfile } from "../context/ProfileContext";

const TABLES = Array.from({ length: 19 }, (_, i) => i + 2);
const LEARN_GAMES = [
  { id: "memory-forge", name: "Memory Forge", desc: "Match & remember", icon: Sparkles, color: "#FFD600" },
  { id: "tower-builder", name: "Tower Builder", desc: "Calm, no pressure", icon: Blocks, color: "#00FF66" },
  { id: "target-blaster", name: "Target Blaster", desc: "Quick recognition", icon: Crosshair, color: "#FF2A7A" },
];

export default function Learn() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const [table, setTable] = useState(null);

  return (
    <AppShell>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-4xl font-black leading-none">Learn</h1>
          <p className="text-[#A8B2D1] text-sm mt-1">Pick a table to practise on its own.</p>
        </div>
        {profile && <MascotCoach mascotId={profile.mascot} message="One table at a time!" />}
      </div>

      <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
        {TABLES.map((t) => (
          <button
            key={t}
            data-testid={`learn-table-${t}`}
            onClick={() => setTable(t)}
            className="press glass rounded-2xl aspect-square flex flex-col items-center justify-center">
            <span className="font-math font-extrabold text-2xl">{t}</span>
            <span className="text-[10px] text-[#A8B2D1]">table</span>
          </button>
        ))}
      </div>

      <AnimatePresence>
        {table && (
          <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ background: "rgba(5,4,14,0.8)", backdropFilter: "blur(6px)" }}
            onClick={() => setTable(null)} data-testid="learn-sheet">
            <motion.div
              initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }}
              transition={{ type: "spring", stiffness: 260, damping: 24 }}
              onClick={(e) => e.stopPropagation()}
              className="glass w-full max-w-md rounded-3xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-black">Practise the <span className="text-[#FF2A7A]">{table}×</span> table</h2>
                <button data-testid="learn-close" onClick={() => setTable(null)}
                        className="w-9 h-9 rounded-full glass flex items-center justify-center"><X size={18} /></button>
              </div>
              <div className="space-y-3">
                {LEARN_GAMES.map((g) => {
                  const Icon = g.icon;
                  return (
                    <button
                      key={g.id}
                      data-testid={`learn-launch-${g.id}`}
                      onClick={() => navigate(`/play/${g.id}?tables=${table}`)}
                      className="w-full press glass rounded-2xl p-4 flex items-center gap-3 text-left">
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center"
                           style={{ background: `${g.color}22` }}>
                        <Icon size={22} style={{ color: g.color }} />
                      </div>
                      <div>
                        <div className="font-bold">{g.name}</div>
                        <div className="text-xs text-[#A8B2D1]">{g.desc}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </AppShell>
  );
}
